// Lib
import React, { ReactElement, useRef } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import Helmet from 'react-helmet';
import { compose } from '../../node_module_clones/recompose';
import { hot } from 'react-hot-loader/root';

// Utils
import { ZoomStateContextProvider } from '../canvas/zoom/ZoomStateContext';
import platformSingleton from '../platform/platformSingleton';
import { getRootType } from '../../common/elements/utils/elementPropertyUtils';

// Actions
import { deselectAllElements } from '../element/selection/selectionActions';

// Hooks
import useAttachPushNotifications from '../capacitor/hooks/useAttachPushNotifications';
import useScrollToCaretOnEdit from './boardsPage/editing/useScrollToCaretOnEdit';
import useAttachNavigation from '../capacitor/hooks/useAttachNavigation';
import useSetCapacitorAuthCookie from '../capacitor/hooks/useSetCapacitorAuthCookie';
import useMobileKeyboardEventHandler from './toolbar/hooks/useMobileKeyboardEventHandler';
import useAttachCapacitorLifecycle from '../capacitor/hooks/useAttachCapacitorLifecycle';
import useAttachCapacitorQuicknotes from '../capacitor/hooks/useAttachCapacitorQuicknotes';
import useFetchHistoryOnMount from '../workspace/header/workspaceToolsHeader/boardSearch/state/useFetchHistoryOnMount';
import useFetchQuickNotesOnMount from './utils/useFetchQuickNotesOnMount';

// Components
import MobileToolbar from './toolbar/MobileToolbar';
import MobileKeyboardAnimationDebugPanel from './debug/MobileKeyboardAnimationDebugPanel';
import MobileCanvasContextMenu from './contextMenu/MobileCanvasContextMenu';
import MobileBoardHeader from './boardsPage/header/MobileBoardHeader';
import MobilePageBody from './structural/MobilePageBody';
import WorkspacePositioner from '../workspace/WorkspacePositioner';
import WorkspaceClipboardHandler from '../workspace/shortcuts/clipboard/WorkspaceClipboardHandler';
import GlobalDragAndDropHelper from '../utils/dnd/GlobalDragAndDropHelper';
import GlobalPinchGestureBlocker from '../utils/ipad/GlobalPinchGestureBlocker';
import CurrentUserSubscriptionSynchroniser from '../user/CurrentUserSubscriptionSynchroniser';
import AnnotationEditor from '../element/drawing/annotation/annotationEditor/LoadableAnnotationEditor';
import WorkspaceTooltipManager from '../components/tooltips/WorkspaceTooltipManager';
import Main from '../workspace/main/Main';
import MobileNotificationsSheet from './notifications/MobileNotificationsSheet';
import MobileQuickNotesPage from '../workspace/quickNotes/MobileQuickNotesPage';
import BillingAlertSheet from './billing/BillingAlertSheet';
import MobileSharingSheet from './sharing/MobileSharingSheet';

import elementShortcutsDecorator from '../workspace/shortcuts/clipboard/elementShortcutsDecorator';

// Types
import { GeneralWorkspaceProps } from '../workspace/workspaceTypes';
import { RootBoardTypes } from '../../common/boards/boardConstants';

// Styles
import './mobile-ui-variables.scss';
import './MobileWorkspace.scss';
import '../workspace/Workspace.scss';
import WorkspaceOnboardingManager from '../workspace/onboarding/WorkspaceOnboardingManager';

const MobileWorkspace = (props: GeneralWorkspaceProps): ReactElement => {
    const { currentBoard, breadcrumbObj } = props;

    const toolbarRef = useRef<HTMLDivElement>(null);
    const mobileWorkspaceRef = useRef<HTMLDivElement>(null);

    const isDevMode = process.env.NODE_ENV === 'development';
    const { isCurrentBoardRoot, isCurrentBoardCached } = props;

    // If the current board is cached - don't show a loader or canvas contents
    const mainComponent = !isCurrentBoardCached ? <Main {...props} /> : null;

    const dispatch = useDispatch();
    // Allow clicking outside the screen to deselect elements in dev mode
    //  -- not actually reachable in the production app
    const dispatchDeselectAll = () => dispatch(deselectAllElements());

    const onWorkspaceClick = (event: React.MouseEvent) => {
        if (mobileWorkspaceRef.current !== event.target) return;
        dispatchDeselectAll();
    };

    const isCurrentBoardQuickNotes = getRootType(currentBoard) === RootBoardTypes.QUICK_NOTES;

    // Add listener for navigation
    useAttachNavigation();

    // Listen for mobile push notification registrations
    useAttachPushNotifications();

    // Keep an edited element within view
    useScrollToCaretOnEdit(toolbarRef);

    // Ensure auth cookie is set
    useSetCapacitorAuthCookie();

    // Listen for keyboard events
    useMobileKeyboardEventHandler();

    // Attach Lifecycle hooks
    useAttachCapacitorLifecycle();

    // Listen for Quicknote actions
    useAttachCapacitorQuicknotes();

    // Fetch history on workspace mount to improve recently viewed speed
    useFetchHistoryOnMount();

    // Fetch quick notes on workspace mount to improve speed of quick notes navigation
    useFetchQuickNotesOnMount();

    return (
        <div className={classNames('MobileWorkspaceWrapper', { 'dev-mode': isDevMode })}>
            <div
                className={classNames('MobileWorkspace', 'Workspace', 'workspace-ready', {
                    'workspace-root-board': isCurrentBoardRoot,
                })}
                onClick={onWorkspaceClick}
                ref={mobileWorkspaceRef}
            >
                <Helmet>
                    <meta
                        name="viewport"
                        content="width=device-width, initial-scale=1.0, maximum-scale=1, user-scalable=no viewport-fit=cover"
                    />
                </Helmet>
                <ZoomStateContextProvider zoomScale={1}>
                    <MobileBoardHeader currentBoard={currentBoard} breadcrumbObj={breadcrumbObj} />

                    {!isCurrentBoardQuickNotes && (
                        <MobilePageBody>
                            <WorkspacePositioner {...props}>{mainComponent}</WorkspacePositioner>
                            <AnnotationEditor />
                        </MobilePageBody>
                    )}

                    {isCurrentBoardQuickNotes && <MobileQuickNotesPage {...props} />}

                    <MobileToolbar
                        {...props}
                        hasEditorSelection={false /* just passing this to stop TS from complaining */}
                        toolbarRef={toolbarRef}
                    />

                    <MobileNotificationsSheet />

                    <MobileSharingSheet {...props} />

                    <MobileKeyboardAnimationDebugPanel mobileWorkspaceRef={mobileWorkspaceRef} />
                    <MobileCanvasContextMenu workspaceRef={mobileWorkspaceRef} />
                    <BillingAlertSheet />

                    <WorkspaceClipboardHandler {...props} />
                    <GlobalDragAndDropHelper />
                    {platformSingleton.features.isTouch ? <GlobalPinchGestureBlocker /> : null}
                    <CurrentUserSubscriptionSynchroniser />

                    <WorkspaceOnboardingManager />

                    <WorkspaceTooltipManager />
                </ZoomStateContextProvider>
            </div>
        </div>
    );
};

const enhance = compose<GeneralWorkspaceProps, GeneralWorkspaceProps>(hot, elementShortcutsDecorator);

export default enhance(MobileWorkspace);
